import * as React from "react";

import { useResizeObserver } from "@/hooks/useResizeObserver";
import { cn } from "@/lib/utils";
import { AppTheme } from "@/utils/constants/theme";
import { cva, VariantProps } from "class-variance-authority";
import { Ban, Eye, EyeOff, Clock, X } from "lucide-react";
import Container from "../Container";
import Text from "../Text";

export type InputVariants =
  | "default"
  | "desctructive"
  | "ghost"
  | "disabled"
  | null
  | undefined;

export type InputProps = React.ComponentProps<"input"> &
  Omit<VariantProps<typeof inputVariants>, "variant"> & {
    /** Optional wrapper class for the outer `div.w-full` */
    containerClassName?: string;
    /** Optional wrapper style for the outer `div.w-full` */
    containerStyle?: React.CSSProperties;
    floatLabel?: boolean;
    labelOutside?: boolean; // Makes the label appear outside the input
    variant?: InputVariants;
    preFix?: string | React.ReactNode;
    postFix?: string | React.ReactNode;
    clearable?: boolean; // Show X icon on the right to clear when there is value
    err?: string;
  };

export const inputVariants = cva(
  `
  file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground flex h-[30px] w-full min-w-0 rounded-[15px] border bg-transparent text-base shadow-[0px_1px_1px_0px_#00000040] transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed md:text-sm
  aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive overflow-hidden
  `,
  {
    variants: {
      variant: {
        default:
          "border-input focus-visible:border-gray-400",
        desctructive:
          "border-input-destructive ring-input-destructive/50 ring-[3px]",
        ghost: "border-0",
        disabled: "border-input bg-input-disabled disabled:cursor-not-allowed",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const ShadInput = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      containerStyle,
      type,
      variant,
      preFix,
      postFix,
      clearable = false,
      floatLabel = true,
      labelOutside = true,
      err,
      required = false,
      disabled,
      ...rest
    },
    ref
  ) => {
    const preFixRef = React.useRef<HTMLDivElement>(null);
    const postFixRef = React.useRef<HTMLDivElement>(null);
    const [preFixD, postFixD] = useResizeObserver([preFixRef, postFixRef]);

    const [showPassword, setShowPassword] = React.useState(false);

    const isTypePassword = type === "password";
    const isTypeTime = type === "time";
    const hasError = !!err;
    const value = rest.value ?? "";
    const showClear = clearable && String(value).length > 0 && !isTypePassword && !isTypeTime;

    return (
      <div className={cn("w-full", containerClassName)} style={containerStyle}>
        {/* Outside label */}
        {labelOutside && (
          <Text
            size={12}
            className="text-gray-700 whitespace-nowrap mb-0.5"
            style={{
              ...(hasError && { color: AppTheme.darkBurgundy }),
            }}
          >
            {rest.placeholder || ""}
            {required && (
              <span className="ml-1" style={{ color: AppTheme.darkBurgundy }}>
                *
              </span>
            )}
          </Text>
        )}

        <div className="relative w-full">
          <input
            {...rest}
            ref={ref} // <-- this line enables `register()` to work
            disabled={disabled}
            type={showPassword ? "text" : type}
            data-slot="input"
            className={cn(
              inputVariants({
                variant: hasError
                  ? "desctructive"
                  : disabled
                  ? "disabled"
                  : variant,
              }),
              className
            )}
            placeholder={
              labelOutside ? "" : floatLabel ? " " : rest.placeholder
            }
            style={{
              padding: "8px 8px",
              fontSize: "12px",
              paddingLeft: preFix ? "32px" : "8px",
              paddingRight:
                !!postFix || !!isTypePassword || !!isTypeTime || showClear
                  ? `${postFixD?.width || preFixD?.width || 0}px`
                  : "8px",
            }}
          />
          {floatLabel && !labelOutside && (
            <div
              className="input-label truncate"
              style={{
                ...(preFix && { left: `${preFixD?.width || 0}px`, padding: 0 }),
                ...(hasError && { color: AppTheme.darkBurgundy }),
              }}
            >
              {rest.placeholder || ""}
              {required && (
                <span
                  className="absolute text-lg right-[-1px] top-[-2.5px]"
                  style={{ color: AppTheme.darkBurgundy }}
                >
                  *
                </span>
              )}
            </div>
          )}
          {preFix && (
            <div ref={preFixRef} className="input-prefix">
              {preFix}
            </div>
          )}
          {(postFix || isTypePassword || isTypeTime || showClear) && (
            <div
              ref={postFixRef}
              className={`input-postfix ${
                isTypeTime ? "pointer-events-none" : ""
              }`}
            >
              {isTypePassword ? (
                !showPassword ? (
                  <EyeOff
                    className="cursor-pointer"
                    onClick={() => setShowPassword((prev) => !prev)}
                  />
                ) : (
                  <Eye
                    className="cursor-pointer"
                    onClick={() => setShowPassword((prev) => !prev)}
                  />
                )
              ) : isTypeTime ? (
                <Clock size={16} className="text-[#836902]" />
              ) : showClear ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    rest.onChange?.({ target: { value: "" } } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  className="p-0.5 rounded hover:bg-gray-200/80 transition-colors flex items-center justify-center text-gray-500 hover:text-gray-700"
                  aria-label="Clear"
                >
                  <X size={16} />
                </button>
              ) : (
                postFix || <></>
              )}
            </div>
          )}
        </div>
        {!!err && (
          <Container
            indention={0}
            flexDisplay
            direction="row"
            itemsAlign="center"
            gap={5}
          >
            <Ban size="14px" color={AppTheme.darkBurgundy} />
            <Text size="xs" color={AppTheme.darkBurgundy}>
              {err}
            </Text>
          </Container>
        )}
      </div>
    );
  }
);

// good devtool hygiene
ShadInput.displayName = "ShadInput";

// Use ref-forwarding Input
const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => (
  <ShadInput {...props} className={cn("border-[#CFCFCF] !text-[12px]", props.className)} ref={ref} />
));

Input.displayName = "Input";

export default Input;
